import type { MailAttachmentAction, MailAttachmentSource } from '@embertime/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Check,
  ChevronRight,
  Download,
  FileText,
  Folder,
  MoreHorizontal,
  Receipt,
} from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { api, type MailAttachment } from '../../lib/api'
import { Button } from '../ui/button'
import { SidePanel } from '../ui/side-panel'

type Stage = 'menu' | 'drive' | 'receipt' | 'result'
/** Parent key includes user/account/message/attachment. Unmount discards choices. */
export function AttachmentActions({
  source,
  attachment,
  userId,
  download,
}: {
  source: MailAttachmentSource
  attachment: MailAttachment
  userId: string
  download: () => Promise<void>
}) {
  const [stage, setStage] = useState<Stage | null>(null)
  const [folderId, setFolder] = useState<string | null>(null)
  const [mandantId, setMandant] = useState<string | null>(null)
  const [result, setResult] = useState<MailAttachmentAction | null>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const generation = useRef(0)
  const queryClient = useQueryClient()
  const queryKey = [
    'mail',
    'attachment-actions',
    userId,
    source.accountId,
    source.messageId,
    source.attachmentId,
  ]
  const options = useQuery({
    queryKey,
    queryFn: () => api.mailAttachmentOptions(source),
    retry: false,
    staleTime: 0,
  })
  // Derive automatic selection from fresh targets; never persist a cached
  // unique target as if the user had explicitly chosen it.
  const selectedMandantId =
    !options.isFetching && !options.isError
      ? (mandantId ??
        (options.data?.targets.length === 1 ? options.data.targets[0].mandantId : null))
      : null
  const close = () => {
    generation.current++
    setStage(null)
    setFolder(null)
    setMandant(null)
    setResult(null)
    trigger.current?.focus()
  }
  const mutation = useMutation({
    networkMode: 'always',
    retry: false,
    mutationFn: async ({
      kind,
      targetId,
    }: {
      kind: 'drive' | 'receipt'
      targetId: string | null
    }) => {
      if (!navigator.onLine)
        throw new Error('Offline: keine Übergabe. Bitte später erneut versuchen.')
      if (kind === 'drive' && targetId) return api.mailAttachmentDrive(source, targetId)
      if (kind === 'receipt' && targetId) return api.mailAttachmentReceipt(source, targetId)
      throw new Error('Bitte ein Ziel auswählen.')
    },
    onMutate: () => generation.current,
    onSuccess: async (action, _kind, context) => {
      if (context !== generation.current) return
      setResult(action)
      setStage('result')
      await queryClient.invalidateQueries({ queryKey })
      await queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
  // Limit focus to this modal on mobile, restore to the attachment on close.
  useEffect(() => {
    if (!stage) return
    const focus = () =>
      document.querySelector<HTMLElement>('[data-attachment-sheet] button')?.focus()
    const frame = requestAnimationFrame(focus)
    const trap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !window.matchMedia('(max-width: 639px)').matches) return
      const surface = document.querySelector('[data-attachment-sheet]')?.closest('[role="dialog"]')
      const buttons = surface?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href]')
      if (!buttons?.length) return
      const first = buttons[0]
      const last = buttons[buttons.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', trap)
    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('keydown', trap)
    }
  }, [stage])
  useEffect(
    () => () => {
      generation.current++
    },
    [],
  )
  const accepted = result && ['angenommen', 'bereitsVorhanden'].includes(result.status)
  const driveActions = options.data?.actions.filter((a) => a.kind === 'drive' && a.documentId) ?? []
  const row = (
    label: string,
    subtitle: string,
    icon: ReactNode,
    action: () => void,
    selected = false,
  ) => (
    <button
      type="button"
      onClick={action}
      disabled={mutation.isPending}
      aria-pressed={stage === 'drive' || stage === 'receipt' ? selected : undefined}
      className={`flex w-full min-h-14 items-center gap-4 rounded-xl border-t border-border-subtle px-3 py-2 text-left ${selected ? 'bg-accent/15 text-accent' : ''}`}
    >
      <span className="shrink-0 text-accent">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold break-words">{label}</span>
        <span className="block text-xs text-text-tertiary break-words">{subtitle}</span>
      </span>
      {selected ? (
        <Check size={18} />
      ) : (
        <ChevronRight size={16} className="shrink-0 text-text-tertiary" />
      )}
    </button>
  )
  const file = (
    <div className="flex items-center gap-3 rounded-xl border border-border-default px-3 py-3 text-xs">
      <FileText className="shrink-0 text-danger" size={18} />
      <span className="min-w-0 break-all">
        {attachment.filename} · {(attachment.sizeBytes / 1024).toFixed(0)} KB
      </span>
    </div>
  )
  if (
    [options.error, mutation.error].some(
      (error) => error && 'code' in error && error.code === 'MAIL_PROTECTED',
    )
  )
    return null
  return (
    <>
      <button
        ref={trigger}
        type="button"
        aria-label={`Aktionen für ${attachment.filename}`}
        className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-accent"
        onClick={() => {
          mutation.reset()
          setStage('menu')
          void options.refetch()
        }}
      >
        <MoreHorizontal size={20} />
      </button>
      {driveActions.map((a) => (
        <a
          key={a.id}
          href={`${import.meta.env.BASE_URL}documents/${a.documentId}`}
          className="inline-flex min-h-11 items-center gap-1 text-xs text-accent"
        >
          <Folder size={14} />
          {a.targetName} · Dokument öffnen
        </a>
      ))}
      <SidePanel
        open={stage !== null}
        onClose={close}
        title={
          stage === 'drive'
            ? 'In EmberDrive ablegen'
            : stage === 'receipt'
              ? 'Als Beleg an die Buchhaltung'
              : stage === 'result'
                ? accepted
                  ? result?.kind === 'drive'
                    ? 'In EmberDrive abgelegt'
                    : 'Beleg übergeben'
                  : result?.status === 'abgelehnt'
                    ? 'Beleg abgelehnt'
                    : 'Übergabe noch ungeklärt'
                : 'Anhang'
        }
        description={
          stage === 'menu'
            ? `${attachment.filename} · ${(attachment.sizeBytes / 1024).toFixed(0)} KB`
            : stage === 'drive'
              ? 'Ordner für diesen Anhang wählen'
              : stage === 'receipt'
                ? 'Mandant für diesen Anhang wählen'
                : accepted
                  ? result?.kind === 'drive'
                    ? 'Mit dieser Mail verknüpft'
                    : 'Die Übergabe an emberfibu ist bestätigt'
                  : 'Es liegt keine Empfangsbestätigung vor'
        }
        className="[&_h2]:[font-family:Georgia,serif] [&_h2]:text-xl [&_footer]:min-h-[92px] [&_footer]:items-start [&_header]:touch-none [&>div:first-child]:touch-none"
        footer={
          stage === 'drive' || stage === 'receipt' ? (
            <>
              <Button
                variant="secondary"
                className="flex-1 min-h-11"
                onClick={() => {
                  // A response already in flight belongs to the abandoned selection.
                  generation.current++
                  mutation.reset()
                  setStage('menu')
                  setFolder(null)
                  setMandant(null)
                }}
              >
                Abbrechen
              </Button>
              <Button
                className="flex-1 min-h-11"
                disabled={
                  mutation.isPending ||
                  options.isFetching ||
                  options.isError ||
                  !(stage === 'drive' ? folderId : selectedMandantId)
                }
                onClick={() =>
                  mutation.mutate({
                    kind: stage,
                    targetId: stage === 'drive' ? folderId : selectedMandantId,
                  })
                }
              >
                {mutation.isPending
                  ? 'Bitte warten …'
                  : stage === 'drive'
                    ? 'Hier ablegen'
                    : 'Beleg übergeben'}
              </Button>
            </>
          ) : stage === 'result' ? (
            <Button className="w-full min-h-11" onClick={close}>
              Fertig
            </Button>
          ) : undefined
        }
      >
        <div data-attachment-sheet className={`space-y-3 ${stage === 'menu' ? 'pb-9' : 'pb-3'}`}>
          {stage === 'menu' && (
            <div>
              {row('Herunterladen', 'Auf diesem Gerät speichern', <Download size={19} />, () => {
                void download()
                close()
              })}
              {row(
                'Nach EmberDrive',
                'Ordner wählen und Mail verknüpfen',
                <Folder size={19} />,
                () => {
                  mutation.reset()
                  setStage('drive')
                },
              )}
              {row('Als Beleg', 'An emberfibu übergeben', <Receipt size={19} />, () => {
                mutation.reset()
                setStage('receipt')
              })}
            </div>
          )}
          {(stage === 'drive' || stage === 'receipt') && (
            <>
              {file}
              {options.isPending || options.isFetching ? (
                <p className="text-sm text-text-secondary">Ziele werden geprüft …</p>
              ) : options.isError ? (
                <p role="alert" className="text-sm text-danger">
                  Aktionen nicht verfügbar. Zugriff und Verbindung erneut prüfen.
                </p>
              ) : stage === 'drive' ? (
                <>
                  <p className="px-2 text-xs uppercase tracking-wide text-text-tertiary">
                    EmberDrive / Dokumente
                  </p>
                  {!options.data?.folders.length && (
                    <p className="text-sm text-text-secondary">
                      Noch keine berechtigten Ordner vorhanden. Bitte einen persönlichen
                      Dokumentordner einrichten lassen.
                    </p>
                  )}
                  <div>
                    {options.data?.folders.map((f) => (
                      <div key={f.id}>
                        {row(
                          f.name,
                          `${f.count} Dokumente`,
                          <Folder size={19} />,
                          () => setFolder(f.id),
                          f.id === folderId,
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="px-2 text-xs text-text-secondary">
                    Die Mail erhält einen Rückverweis auf das abgelegte Dokument.
                  </p>
                </>
              ) : (
                <>
                  {!options.data?.receiptAvailable ? (
                    <p role="status" className="text-sm text-text-secondary">
                      Der Belegeingang ist noch nicht angeschlossen. Es wird nichts übergeben.
                    </p>
                  ) : !options.data.targets.length ? (
                    <p className="text-sm text-text-secondary">
                      Kein berechtigter Mandant verfügbar.
                    </p>
                  ) : (
                    <div>
                      {options.data.targets.map((t) => (
                        <div key={t.mandantId}>
                          {row(
                            t.name,
                            t.destination,
                            <Receipt size={19} />,
                            () => setMandant(t.mandantId),
                            t.mandantId === selectedMandantId,
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-text-secondary">
                    Ohne bestätigte Auswahl erfolgt keine Übergabe.
                  </p>
                </>
              )}
            </>
          )}
          {mutation.isError && (
            <p role="alert" className="text-sm text-danger">
              Die Aktion konnte nicht bestätigt werden. Prüfe die Verbindung und versuche es erneut.
            </p>
          )}
          {stage === 'result' && result && (
            <>
              {accepted && (
                <div className="rounded-xl border border-success/30 bg-success/10 p-3 text-success">
                  <p className="flex items-center gap-2 text-sm font-semibold">
                    <Check size={18} />
                    {result.kind === 'drive' ? 'Dokument abgelegt' : 'Im Belegpostfach eingegangen'}
                  </p>
                  {result.kind === 'receipt' && (
                    <p className="mt-1 text-xs text-text-secondary">
                      Die Verarbeitung in emberfibu erfolgt anschließend.
                    </p>
                  )}
                </div>
              )}
              {file}
              {result.kind === 'receipt' && (
                <dl className="rounded-xl border border-border-default p-3 text-xs">
                  <div className="flex justify-between gap-3 py-1">
                    <dt className="text-text-tertiary">Mandant</dt>
                    <dd className="break-words text-right">{result.targetName}</dd>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-border-subtle py-1">
                    <dt className="text-text-tertiary">Ziel</dt>
                    <dd>{result.destination}</dd>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-border-subtle py-1">
                    <dt className="text-text-tertiary">Herkunft</dt>
                    <dd>Mit dieser Mail verknüpft</dd>
                  </div>
                </dl>
              )}
              {result.status === 'unklar' && (
                <>
                  <p className="text-sm text-text-secondary">
                    Der Ausgang ist unklar. Beim erneuten Prüfen wird derselbe Vorgang abgefragt und
                    kein neuer Beleg gesendet.
                  </p>
                  <Button
                    variant="outline"
                    disabled={mutation.isPending}
                    onClick={() => mutation.mutate({ kind: 'receipt', targetId: result.mandantId })}
                  >
                    Status prüfen
                  </Button>
                </>
              )}
              {driveActions.length > 0 && (
                <>
                  <p className="px-2 text-xs uppercase tracking-wide text-text-tertiary">
                    {result.kind === 'receipt' ? 'Auch in EmberDrive' : 'EmberDrive'}
                  </p>
                  {driveActions.map((a) => (
                    <a
                      key={a.id}
                      href={`${import.meta.env.BASE_URL}documents/${a.documentId}`}
                      className="flex min-h-14 items-center gap-3 px-3 text-sm"
                    >
                      <Folder size={20} className="text-accent" />
                      <span>
                        {a.targetName}
                        <span className="block text-xs text-text-tertiary">
                          Dokument öffnen · Rückverweis zur Mail
                        </span>
                      </span>
                      <ChevronRight size={16} />
                    </a>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </SidePanel>
    </>
  )
}
